import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { MdInfoOutline } from 'react-icons/md';
import { istVorVeranstaltungsbeginn } from './teilnahmedatum';

/**
 * Sperrt das Melden, bis die Veranstaltung begonnen hat - und sagt, ab wann es geht.
 *
 * Eine Punktemeldung ist die Aussage "diese Person **war** da". Vor der Beginnzeit ist sie eine
 * Planung, und die Kammern weisen solche Punkte zurueck. Das Bittere daran ist, dass davon in der
 * App bisher nichts ankam: die Veranstalter-API des EIV nimmt die Meldung mit **200** an, die
 * Teilnahme faellt daraufhin aus `erfassteTeilnahmen` - die Warteschlange ist leer, alles sieht
 * erledigt aus -, und erst die Kammer verweigert die Punkte, wenn sie sie abholt. Kein
 * Fehlercode, keine Zeile, kein zweiter Versuch.
 *
 * Deshalb eine **Sperre und keine Warnung**: eine Warnung liesse genau den Klick zu, der die
 * Punkte still kostet, und zurueckholen laesst sich eine Meldung nicht.
 *
 * Am **Erfassen** aendert das nichts. Der Tag entscheidet ueber das Erfassen, die Uhrzeit ueber
 * das Melden (siehe `teilnahmedatum.js`).
 */
const HOECHSTE_WARTEZEIT = 6 * 60 * 60 * 1000;

/**
 * Nur die Uhrzeit, kein Datum: die Zeile erscheint ausschliesslich am Veranstaltungstag - an
 * jedem frueheren ersetzt `VeranstaltungLiegtInZukunft` die halbe Seite.
 */
export const meldenGesperrtHinweis = (veranstaltung, jetzt = Date.now()) => {
	if(!istVorVeranstaltungsbeginn(veranstaltung, jetzt)) return null;
	return {
		kopf: `Melden ab ${moment(veranstaltung.beginn).format('HH:mm')} Uhr.`,
		satz: "Vor dem Beginn wäre die Meldung eine Planung, keine Tatsache – die Kammer weist "
			+ "solche Punkte zurück. Erfassen können Sie schon jetzt." };
};

/**
 * Millisekunden bis zum naechsten Nachsehen, `null` wenn nichts mehr zu warten ist.
 *
 * Als reine Funktion, damit die Weckerei pruefbar ist, ohne einen Hook zu rendern. **Gedeckelt**,
 * weil `setTimeout` jenseits von ~24,8 Tagen ueberlaeuft und sofort feuert - zusammen mit der
 * erneuten Pruefung waere das eine Endlosschleife. Der Deckel ist kein fachlicher Rhythmus, nur
 * eine Sicherung; er macht aus einem weit entfernten Beginn ein paar harmlose Ticks pro Tag.
 */
export const naechsteWartezeit = (veranstaltung, jetzt = Date.now()) => {
	if(!istVorVeranstaltungsbeginn(veranstaltung, jetzt)) return null;
	return Math.min(moment(veranstaltung.beginn).valueOf() - jetzt, HOECHSTE_WARTEZEIT);
};

/**
 * Die Sperre loest sich zur Beginnzeit von selbst, ohne Neuladen.
 *
 * Genau **einmal** aufrufen und den Wert an Hinweis und beide Meldeknoepfe weitergeben: zwei
 * Aufrufe hiessen zwei Timer, die auseinanderlaufen koennen. Und **vor** einem fruehen `return`
 * der Seite, sonst wechselt die Hook-Reihenfolge zwischen Rendern.
 */
export const useMeldenGesperrt = (veranstaltung) => {
	// Lazy: der erste Render ist bereits richtig. Aus false in einem useEffect nach true zu
	// wechseln liesse den Meldeknopf einen Wimpernschlag lang aktiv aussehen.
	const [gesperrt, setGesperrt] = useState(() => istVorVeranstaltungsbeginn(veranstaltung));
	// Abhaengigkeit ist der Beginn, nicht das Objekt - der Store liefert bei jedem Lauf eine neue
	// Referenz, und die wuerde den Timer bei jeder Aktion neu aufsetzen.
	const beginn = veranstaltung && veranstaltung.beginn;
	useEffect(() => {
		let timer;
		// Kein Zuschlag auf die Wartezeit: nach dem Feuern wird neu geprueft. War die Uhr eine
		// Millisekunde zu frueh oder hat jemand die Systemzeit verstellt, wird neu geplant.
		const pruefe = () => {
			const wartezeit = naechsteWartezeit({ beginn });
			setGesperrt(wartezeit !== null);
			if(wartezeit !== null) timer = setTimeout(pruefe, wartezeit);
		};
		pruefe();
		return () => clearTimeout(timer);
	}, [beginn]);
	return gesperrt;
};

/**
 * Kein reactstrap-`Alert`: dessen `role="alert"` laesst sich nicht ueberschreiben und unterbricht
 * den Screenreader mitten im Satz. Und der Grund steht als **Text im Dokument**, nicht nur im
 * `title` - ein `disabled`-Knopf ist nicht fokussierbar, wer ihn per Tastatur nicht findet, liest
 * hier trotzdem, warum.
 */
const MeldenErstAbBeginn = ({ veranstaltung }) => {
	const hinweis = meldenGesperrtHinweis(veranstaltung);
	if(hinweis === null) return null;
	return (
		<div className="alert alert-secondary py-2 mb-2 d-flex align-items-start gap-2"
			role="status" aria-live="polite">
			<MdInfoOutline fontSize="1.3em" className="flex-shrink-0 mt-1" aria-hidden="true" />
			<div><b>{hinweis.kopf}</b> {hinweis.satz}</div>
		</div>
	);
};

export default MeldenErstAbBeginn;
